import { AntDesign } from "@expo/vector-icons";
import { useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, View, FlatList, Pressable, TextInput } from "react-native";
import { useDispatch } from "react-redux";

import Loader from "@/components/Loader";
import TitleXl from "@/components/TitleXl";
import { setForm } from "@/context/actions/actions";
import i18n from "@/locales/i18n";
import { sendRequest } from "@/utils/ApiUtil";
import { checkTransferType } from "@/utils/transactions/TransactionsUtils";

interface TransactionsListProps {
  accountId: string;
}

export default function TransactionsList(props: TransactionsListProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const page = 0;
  const pageSize = 100;
  const pageLimit = 10;

  useFocusEffect(
    React.useCallback(() => {
      loadTransactions();
      setIsFetching(true);
      return () => {
        setTransactions([]);
        setIsLoading(true);
        setIsFetching(false);
        setCurrentPage(1);
        setSearchQuery("");
      };
    }, [isFetching]),
  );

  const fetchTransactions = async (size: number) => {
    try {
      const response = await sendRequest(
        "get",
        "/admin/transactions",
        { accountId: props.accountId, page: size, pageSize },
        true,
        true,
      );
      if (response) {
        return response;
      } else {
        return [];
      }
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const loadTransactions = async () => {
    if (isFetching) return;
    let moreTransactions = true;
    let currentPage = page;
    let newTransactionsList = [...transactions];

    while (moreTransactions) {
      const newContacts = await fetchTransactions(currentPage);

      const uniqueNewTransactions = newContacts?.filter(
        (newTransaction) =>
          !transactions.some(
            (transaction) => transaction.id === newTransaction.id,
          ),
      );

      newTransactionsList = [...newTransactionsList, ...uniqueNewTransactions];

      if (uniqueNewTransactions.length === 0 || newContacts.length < pageSize) {
        moreTransactions = false;
      } else {
        currentPage += 1;
      }
    }

    if (newTransactionsList.length > transactions.length) {
      setTransactions(newTransactionsList);
      dispatch(setForm({ transactions: newTransactionsList }));
    }
    setIsLoading(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderTransaction = ({ item }: { item: any }) => (
    <View className="w-full flex-row border border-gray bg-gray hover:bg-lightgray rounded-lg bg-gray rounded-lg p-2 mt-2">
      <View className="w-1/5 items-start">
        <Text className="text-sm">
          {new Date(item.transactionPostedDate).toLocaleString(i18n.language, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          })}
        </Text>
      </View>
      <View className="w-1/5 items-center">
        <Text numberOfLines={1} ellipsizeMode="tail" className="text-xs">
          {item.id}
        </Text>
      </View>
      <View className="w-1/5 items-center">
        <Text className="text-sm">
          {new Intl.NumberFormat(i18n.language, {
            style: "currency",
            currency: "USD",
          }).format(item.value)}
        </Text>
      </View>
      <View className="w-1/5 items-center">
        <Text className="text-sm">
          {checkTransferType(
            item.description || "",
            item.categoryType || "",
            item.railInformation?.rail || "",
            t,
          )}
        </Text>
      </View>
      <View className="w-1/5 items-center">
        <Text numberOfLines={1} ellipsizeMode="tail" className="text-sm">
          {item.destination?.profile?.name
            ? item.destination?.profile?.name
            : item.originator?.profile?.name
              ? item.originator?.profile?.name
              : "-"}
        </Text>
      </View>
    </View>
  );

  const renderPageIndicator = ({ item }: { item: number }) => (
    <Pressable onPress={() => handlePageChange(item)} className="mx-1">
      <View
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          currentPage === item ? "bg-purple1" : "bg-gray"
        }`}
      >
        <Text
          className={`${currentPage === item ? "text-white" : "text-black1"}`}
        >
          {item}
        </Text>
      </View>
    </Pressable>
  );

  const filteredTransactions = transactions
    ? transactions.filter(
        (transaction) =>
          transaction.id?.toLowerCase().includes(searchQuery?.toLowerCase()),
      )
    : [];

  const paginatedTransactions = (
    searchQuery ? filteredTransactions : transactions
  ).slice((currentPage - 1) * pageLimit, currentPage * pageLimit);

  return (
    <View className="w-full border border-gray rounded-lg mt-4 p-4 items-center shadow">
      <View className="mb-4">
        <TitleXl text={t("management/customer:transactions.title")} />
      </View>
      <View className="w-full">
        {isLoading ? (
          <View className="m-4">
            <Loader />
          </View>
        ) : (
          <View className="w-full items-center">
            <View className="w-full items-start">
              <View className="absolute left-4 top-3">
                <AntDesign name="search1" size={16} color="#BDBDBD" />
              </View>
              <TextInput
                className="border border-midgray rounded-full pl-10 py-2 text-base"
                placeholder={t("common:terms.searchtransactionid")}
                placeholderTextColor="#BDBDBD"
                onChangeText={(text) => setSearchQuery(text)}
              />
            </View>
            <View className="w-full items-center mt-4">
              <View className="w-full flex-row">
                <View className="w-1/5 items-center">
                  <Text className="text-sm text-darkergray font-thin">
                    {t("management/customer:transactions.fields.date")}
                  </Text>
                </View>
                <View className="w-1/5 items-center">
                  <Text className="text-sm text-darkergray font-thin">
                    {t("management/customer:transactions.fields.id")}
                  </Text>
                </View>
                <View className="w-1/5 items-center">
                  <Text className="text-sm text-darkergray font-thin">
                    {t("management/customer:transactions.fields.amount")}
                  </Text>
                </View>
                <View className="w-1/5 items-center">
                  <Text className="text-sm text-darkergray font-thin">
                    {t("management/customer:transactions.fields.type")}
                  </Text>
                </View>
                <View className="w-1/5 items-center">
                  <Text className="text-sm text-darkergray font-thin">
                    {t("management/customer:transactions.fields.sender")}
                  </Text>
                </View>
              </View>
              <FlatList
                data={paginatedTransactions}
                renderItem={renderTransaction}
                keyExtractor={(item) => item.id.toString()}
                className="w-full"
              />
              <FlatList
                horizontal
                data={Array.from(
                  { length: Math.ceil(transactions.length / pageLimit) },
                  (_, i) => i + 1,
                )}
                renderItem={renderPageIndicator}
                keyExtractor={(item) => item.toString()}
                contentContainerClassName="mt-4 w-full items-center"
              />
            </View>
          </View>
        )}
      </View>
    </View>
  );
}
